import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    fetchDicomListTargets,
    fetchDicomRetrieve,
} from '~/backend/connector-next-api/connector-next-components';
import { fetchSeriesZipBlob } from '~/backend/connector-next-api/dataRetrieval';
import { withRetryOn202 } from '~/backend/retryOn202';
import { useTasksRegistration } from '~/backend/connector-next-api/connector-next-components';
import {
    ConnectorNextContext,
    useConnectorNextContext,
} from '~/backend/connector-next-api/connector-next-context';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { useSnapshotInputApprovalStatus } from '~/hooks/workflows/useSnapshotInputApprovalStatus';
import { Job } from '~/state/atoms/jobAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useCheckTaskInputs from '../useCheckTaskInputs';
import { handleTaskStartError, requireTaskId } from '../utils';

export type ConnectorContourPropagationTaskInput = {
    selectedModel?: PredictionModel;
    selectedStructures?: string[];
    selectedCustomization?: string;
    patientId: string;
    sourceImageStudyUid?: string;
    sourceImageSeriesUid?: string;
    sourceStructureSetUid?: string;
    targetImageStudyUid?: string;
    targetImageSeriesUid?: string;
};

/**
 * A hook for creating a contour propagation task via Connector API.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useContourPropagationTask() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startRegistration } = useTasksRegistration();
    const { processImportedFiles } = useProcessDicomFiles();
    const checkTaskInputs = useCheckTaskInputs();
    const { fetcherOptions } = useConnectorNextContext();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const { showBusy, hideBusy } = useBusyIndicator();
    const snapshotInputApprovalStatus = useSnapshotInputApprovalStatus();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentStructureSet(
                    processedItems.structureSets,
                    selectedPatientId ?? ''
                );
            }
        }
    };

    /**
     * Creates the contour propagation task with the provided input.
     * @param {ConnectorContourPropagationTaskInput} input - The input for the task.
     */
    const start = async (input: ConnectorContourPropagationTaskInput) => {
        if (isSubmitting) {
            return;
        }

        if (!input.selectedModel) {
            handleError(new Error('No model selected.'));
            return;
        }
        if (!input.sourceImageSeriesUid || !input.targetImageSeriesUid) {
            handleError(
                new Error('Source or target image series UID missing.')
            );
            return;
        }
        if (!input.sourceImageStudyUid || !input.targetImageStudyUid) {
            handleError(new Error('Source or target image study UID missing.'));
            return;
        }
        if (!input.sourceStructureSetUid) {
            const message = 'Adapt+ needs a structure set on the source image.';
            handleError(new Error(message), message);
            return;
        }

        setIsSubmitting(true);
        const inputApprovalStatus = await snapshotInputApprovalStatus(
            input.sourceStructureSetUid
        );
        try {
            const inputs = await checkTaskInputs({
                patientId: input.patientId,
                seriesInstanceUids: [input.sourceImageSeriesUid],
                retrievableSeriesInstanceUids: [input.targetImageSeriesUid],
                structureSetSopInstanceUids: [input.sourceStructureSetUid],
            });

            // In session-based workflows the connector holds only the
            // primary image, so a target it lacks is retrieved via DICOM
            // C-MOVE before the registration task is submitted. A target
            // that is not loaded here is then loaded for review.
            const targetIsHeld = inputs.heldSeriesInstanceUids.has(
                input.targetImageSeriesUid
            );
            const targetIsLoaded = !!patientRepository
                .getByPatientId(input.patientId)
                ?.images.some(
                    (image) => image.seriesUid === input.targetImageSeriesUid
                );
            let targetImageBlob: Blob | undefined;
            if (!targetIsHeld || !targetIsLoaded) {
                await showBusy('Downloading target image...');
                try {
                    if (!targetIsHeld) {
                        await retrieveSeries(
                            input.targetImageStudyUid,
                            input.targetImageSeriesUid,
                            fetcherOptions
                        );
                    }
                    if (!targetIsLoaded) {
                        targetImageBlob = await withRetryOn202(() =>
                            fetchSeriesZipBlob(
                                input.targetImageStudyUid!,
                                input.targetImageSeriesUid!
                            )
                        );
                    }
                } finally {
                    hideBusy();
                }
            }

            const modelIdentifier = input.selectedCustomization
                ? `${input.selectedModel.name}.${input.selectedCustomization}`
                : input.selectedModel.name;

            const task = await startRegistration({
                body: {
                    modelName: modelIdentifier,
                    sourceImageSeries: {
                        patientID: input.patientId,
                        seriesInstanceUID: input.sourceImageSeriesUid,
                        studyInstanceUID: input.sourceImageStudyUid,
                    },
                    destinationImageSeries: {
                        patientID: input.patientId,
                        seriesInstanceUID: input.targetImageSeriesUid,
                        studyInstanceUID: input.targetImageStudyUid,
                    },
                    structureSet: {
                        sopInstanceUID: inputs.storedStructureSetUid(
                            input.sourceStructureSetUid
                        ),
                        patientID: input.patientId,
                    },
                },
            });

            const newJob: NewJobData = {
                name: `Adapt+`,
                id: requireTaskId(task),
                type: WorkflowTypes.ADAPT,
                task: {
                    model: input.selectedModel,
                    output: task,
                },
                onLoad: handleProcessedFiles,
                inputApprovalStatus,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            // Load the already-downloaded destination image into the UI
            if (targetImageBlob) {
                const files = [
                    new File(
                        [targetImageBlob],
                        `${input.targetImageSeriesUid}.zip`
                    ),
                ];
                processImportedFiles(files).catch((err) =>
                    handleError(
                        err,
                        `Couldn't process DICOM files for ${input.targetImageSeriesUid}.`
                    )
                );
            }
        } catch (error) {
            handleTaskStartError(
                error,
                'Failed to start contour propagation task.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}

/**
 * Pulls a series into the connector from its inbound DICOM peer via C-MOVE.
 *
 * @param studyInstanceUid - Study of the series.
 * @param seriesInstanceUid - Series to retrieve.
 * @param fetcherOptions - Request options from the connector-next context.
 * @throws If no inbound DICOM target is configured.
 */
async function retrieveSeries(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    fetcherOptions: ConnectorNextContext['fetcherOptions']
): Promise<void> {
    const targets = await fetchDicomListTargets(fetcherOptions);
    const inbound = targets.find((target) => target.isInput)?.name;
    if (!inbound) {
        throw new Error('No inbound DICOM target is configured.');
    }

    await fetchDicomRetrieve({
        ...fetcherOptions,
        body: {
            target: inbound,
            studyInstanceUid,
            seriesInstanceUid,
        },
    });
}
